// One persistent step in the assistant's chain of thought.
//
// Rendered as the sketch's "tool receipt" pill: panel surface, quiet
// border, lime tick dot, muted action label and faint meta counts.
// The in-flight step pulses its dot so the user can tell at a glance
// what is currently running.

import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { surfacesFor } from './uiTokens';


// Split the step into an action label and a faint meta suffix so the
// pill can render them in two tones. The Sources accordion at the
// bottom of the message has the full JSON spec; this is the headline.
function stepParts(block) {
  const name = block.name;
  const spec = block.spec || {};
  if (block.status === 'error') {
    return {
      label: `${labelFor(name)} failed`,
      meta: block.error || '',
    };
  }
  if (name === 'run_sql') {
    const keys = Array.isArray(spec.keys) ? spec.keys : null;
    const keyStr = keys && keys.length && keys !== 'ALL'
      ? keys.slice(0, 3).join(', ') + (keys.length > 3 ? `, +${keys.length - 3}` : '')
      : 'all signals';
    const meta = [
      spec.agg || null,
      spec.group_by && spec.group_by !== 'none' ? `per ${spec.group_by}` : null,
      block.row_count != null ? `${block.row_count} rows` : null,
    ].filter(Boolean).join(' · ');
    return { label: `Queried ${keyStr}`, meta };
  }
  if (name === 'render_chart') {
    const meta = [spec.kind || null, spec.title || null].filter(Boolean).join(' · ');
    return { label: 'Drew chart', meta };
  }
  if (name === 'render_table') {
    const meta = [
      Array.isArray(spec.columns) ? `${spec.columns.length} cols` : null,
      block.row_count != null ? `${block.row_count} rows` : null,
    ].filter(Boolean).join(' · ');
    return { label: 'Built table', meta };
  }
  if (name === 'import_file') {
    // Synthetic receipt for a CSV the user attached to their question.
    const meta = block.row_count != null
      ? `${Number(block.row_count).toLocaleString()} rows`
      : '';
    return { label: `Imported ${spec.filename || 'file'}`, meta };
  }
  if (name === 'execute_code') {
    const ms = Number(spec.duration_ms);
    const meta = [
      spec.lines ? `${spec.lines} lines` : null,
      Number.isFinite(ms) && ms > 0
        ? (ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`)
        : null,
    ].filter(Boolean).join(' · ');
    return { label: 'Ran code', meta };
  }
  // Platform (MCP) tools: the backend sends a human label
  // ("Creating goal", "Running scenario", ...) - prefer it over the
  // raw tool name.
  return { label: block.label || name || 'Tool call', meta: '' };
}


function labelFor(name) {
  if (name === 'run_sql') return 'Query';
  if (name === 'render_chart') return 'Chart';
  if (name === 'render_table') return 'Table';
  if (name === 'import_file') return 'Import';
  if (name === 'execute_code') return 'Code run';
  return name || 'Tool';
}


const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.25; }
`;


const ToolStep = ({ block }) => {
  if (!block) return null;
  const isRunning = block.status === 'running';
  const isError   = block.status === 'error';
  const { label, meta } = stepParts(block);

  return (
    <Stack
      direction="row" alignItems="center" spacing={0.9}
      sx={(theme) => {
        const s = surfacesFor(theme);
        return {
          display: 'inline-flex',
          px: '11px', py: '5px',
          borderRadius: 999,
          border: `1px solid ${s.border}`,
          bgcolor: s.panel,
          maxWidth: '100%',
          transition: 'border-color 120ms',
          '&:hover': { borderColor: s.border2 },
        };
      }}
    >
      {isError ? (
        <ErrorOutlineIcon sx={{ fontSize: 12, color: 'error.main', flexShrink: 0 }} />
      ) : (
        <Box
          sx={(theme) => ({
            width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
            bgcolor: theme.palette.mode === 'dark'
              ? (theme.brand?.limeDark || '#85d818')
              : (theme.brand?.teal || theme.palette.primary.main),
            animation: isRunning ? `${pulse} 1.2s ease-in-out infinite` : 'none',
          })}
        />
      )}
      <Typography
        noWrap
        sx={(theme) => {
          const s = surfacesFor(theme);
          return {
            fontSize: 11.5,
            fontStyle: isRunning ? 'italic' : 'normal',
            color: isError ? theme.palette.error.main : s.muted,
            lineHeight: 1.4,
          };
        }}
      >
        {isRunning ? `${block.label || labelFor(block.name)}…` : label}
      </Typography>
      {!isRunning && meta && (
        <Typography
          noWrap
          sx={(theme) => ({
            fontSize: 11,
            color: surfacesFor(theme).faint,
            lineHeight: 1.4,
          })}
        >
          {meta}
        </Typography>
      )}
    </Stack>
  );
};

// Backward-compat: some callers (the old streaming branch) imported
// ToolShimmer; we keep the named export so nothing breaks even
// though the new ToolStep handles both states.
export const ToolShimmer = ToolStep;
export default ToolStep;
